import type { OrgOverview, RoleGrant, RoleSummary, ScopeType } from "@procuremate/shared";
import { useTranslation } from "react-i18next";
import { SelectField } from "@/components/ui/select-field";

export interface RoleGrantDraft {
  roleKey: string;
  scopeType: ScopeType | "";
  scopeId: string;
}

export const EMPTY_GRANT: RoleGrantDraft = { roleKey: "", scopeType: "", scopeId: "" };

/** Turns the form state into an API grant, or null while incomplete. */
export function toRoleGrant(draft: RoleGrantDraft, roles: readonly RoleSummary[]): RoleGrant | null {
  const role = roles.find((r) => r.key === draft.roleKey);
  if (!role) return null;
  if (role.scopeTypes.includes("GLOBAL")) return { roleKey: role.key, scopeType: "GLOBAL", scopeId: null };
  if (!draft.scopeType || draft.scopeType === "GLOBAL" || !draft.scopeId) return null;
  return { roleKey: role.key, scopeType: draft.scopeType, scopeId: draft.scopeId };
}

/**
 * Role + scope picker. Organisation-wide roles need nothing else; scoped roles (buyer)
 * ask for a legal entity or business unit, listing only active ones (ADR-0012).
 */
export function RoleGrantFields({
  idPrefix,
  value,
  onChange,
  roles,
  org,
  error,
}: {
  idPrefix: string;
  value: RoleGrantDraft;
  onChange: (draft: RoleGrantDraft) => void;
  roles: readonly RoleSummary[];
  org: OrgOverview | undefined;
  error?: string | undefined;
}) {
  const { t } = useTranslation();
  const role = roles.find((r) => r.key === value.roleKey);
  const scoped = role !== undefined && !role.scopeTypes.includes("GLOBAL");
  const scopeTypes = role?.scopeTypes.filter((s) => s !== "GLOBAL") ?? [];
  const targets =
    value.scopeType === "LEGAL_ENTITY"
      ? (org?.legalEntities ?? []).filter((o) => o.active)
      : value.scopeType === "BUSINESS_UNIT"
        ? (org?.businessUnits ?? []).filter((o) => o.active)
        : [];

  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <SelectField
        id={`${idPrefix}-role`}
        label={t("admin.roles.role")}
        placeholder={t("admin.roles.chooseRole")}
        value={value.roleKey}
        error={error}
        options={roles.map((r) => ({ value: r.key, label: t(`admin.roleNames.${r.key}`, { defaultValue: r.name }) }))}
        onChange={(event) => onChange({ roleKey: event.target.value, scopeType: "", scopeId: "" })}
      />
      {scoped ? (
        <>
          <SelectField
            id={`${idPrefix}-scope-type`}
            label={t("admin.roles.scopeType")}
            placeholder={t("admin.roles.chooseScopeType")}
            value={value.scopeType}
            options={scopeTypes.map((s) => ({ value: s, label: t(`admin.scopeTypes.${s}`) }))}
            onChange={(event) => onChange({ ...value, scopeType: event.target.value as ScopeType, scopeId: "" })}
          />
          <SelectField
            id={`${idPrefix}-scope`}
            label={t("admin.roles.scope")}
            placeholder={targets.length ? t("admin.roles.chooseScope") : t("admin.roles.noScopes")}
            value={value.scopeId}
            disabled={!value.scopeType || targets.length === 0}
            options={targets.map((o) => ({ value: o.id, label: `${o.code} — ${o.name}` }))}
            onChange={(event) => onChange({ ...value, scopeId: event.target.value })}
          />
        </>
      ) : role ? (
        <p className="self-end pb-3 text-sm text-fg-muted sm:col-span-2">{t("admin.roles.orgWide")}</p>
      ) : null}
    </div>
  );
}
